import React, { useState, useEffect } from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const AdminBooklets = () => {
  const { user } = useAuth();
  const [booklets, setBooklets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingBooklet, setEditingBooklet] = useState(null);
  const [newBooklet, setNewBooklet] = useState({
    title: '',
    description: '',
    graduation_year: new Date().getFullYear(),
    file: null
  });

  const canManage = user?.role === 'admin' || user?.role === 'editor';

  useEffect(() => {
    fetchBooklets();
  }, []);

  const fetchBooklets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/booklets');
      setBooklets(response.data);
    } catch (error) {
      console.error('Error fetching booklets:', error);
      toast.error('Failed to fetch booklets');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      const maxSize = 100 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`File too large! Maximum size is 100MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        setNewBooklet({ ...newBooklet, file: null });
        e.target.value = '';
        return;
      }
      setNewBooklet({ ...newBooklet, file });
    } else {
      toast.error('Please select a PDF file');
      setNewBooklet({ ...newBooklet, file: null });
      e.target.value = '';
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!newBooklet.file) {
      toast.error('Please select a PDF file');
      return;
    }

    if (!newBooklet.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    const formData = new FormData();
    formData.append('booklet', newBooklet.file);
    formData.append('title', newBooklet.title);
    formData.append('description', newBooklet.description);
    formData.append('graduation_year', newBooklet.graduation_year);

    setUploading(true);
    const uploadToast = toast.loading('Uploading booklet... Please wait...');
    
    try {
      await api.post('/booklets/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 180000
      });
      
      toast.update(uploadToast, { 
        render: 'Booklet uploaded successfully!', 
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
      
      setNewBooklet({
        title: '',
        description: '',
        graduation_year: new Date().getFullYear(),
        file: null
      });
      setShowUploadForm(false);
      fetchBooklets();
    } catch (error) {
      console.error('Error uploading booklet:', error);
      let errorMessage = 'Failed to upload booklet';
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout. The file might be too large or the connection is slow.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.update(uploadToast, { 
        render: errorMessage, 
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (booklet) => {
    setEditingBooklet(booklet);
    setNewBooklet({
      title: booklet.title,
      description: booklet.description || '',
      graduation_year: booklet.graduation_year || new Date().getFullYear(),
      file: null
    });
    setShowUploadForm(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!newBooklet.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setUploading(true);
    const updateToast = toast.loading('Updating booklet...');
    
    try {
      const formData = new FormData();
      formData.append('title', newBooklet.title);
      formData.append('description', newBooklet.description);
      formData.append('graduation_year', newBooklet.graduation_year);
      
      if (newBooklet.file) {
        formData.append('booklet', newBooklet.file);
      }
      
      await api.put(`/booklets/${editingBooklet.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.update(updateToast, { 
        render: 'Booklet updated successfully!', 
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
      
      setNewBooklet({
        title: '',
        description: '',
        graduation_year: new Date().getFullYear(),
        file: null
      });
      setEditingBooklet(null);
      setShowUploadForm(false);
      fetchBooklets();
    } catch (error) {
      console.error('Error updating booklet:', error);
      toast.update(updateToast, { 
        render: 'Failed to update booklet', 
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booklet?')) return;

    try {
      await api.delete(`/booklets/${id}`);
      toast.success('Booklet deleted successfully');
      fetchBooklets();
    } catch (error) {
      console.error('Error deleting booklet:', error);
      toast.error('Failed to delete booklet');
    }
  };

  const handleDownload = (booklet) => {
    try {
      window.open(api.defaults.baseURL + `/booklets/download/${booklet.id}`, '_blank');
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading booklet:', error);
      toast.error('Failed to download booklet');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Manage Booklets</h1>
          <p className="text-gray-600 mt-1">Upload, edit, and manage graduation program booklets</p>
        </div>
        <Button onClick={() => {
          setEditingBooklet(null);
          setNewBooklet({
            title: '',
            description: '',
            graduation_year: new Date().getFullYear(),
            file: null
          });
          setShowUploadForm(!showUploadForm);
        }}>
          {showUploadForm ? 'Cancel' : '+ Upload Booklet'}
        </Button>
      </div>

      {/* Upload/Edit Form */}
      {showUploadForm && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingBooklet ? 'Edit Booklet' : 'Upload New Booklet'}
          </h2>
          <form onSubmit={editingBooklet ? handleUpdate : handleUpload}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Title *"
                value={newBooklet.title}
                onChange={(e) => setNewBooklet({...newBooklet, title: e.target.value})}
                placeholder="e.g., 2024 Graduation Program"
                required
              />

              <Input
                label="Graduation Year"
                type="number"
                value={newBooklet.graduation_year}
                onChange={(e) => setNewBooklet({...newBooklet, graduation_year: e.target.value})}
                placeholder="e.g., 2024"
              />

              <div className="md:col-span-2">
                <label className="label">Description</label>
                <textarea
                  className="input-field"
                  rows="3"
                  value={newBooklet.description}
                  onChange={(e) => setNewBooklet({...newBooklet, description: e.target.value})}
                  placeholder="Brief description of the booklet..."
                />
              </div>

              {!editingBooklet && (
                <div className="md:col-span-2">
                  <label className="label">PDF File * (Max 100MB)</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                  {newBooklet.file && (
                    <p className="text-xs text-blue-600 mt-1">
                      Selected: {newBooklet.file.name} ({(newBooklet.file.size / (1024 * 1024)).toFixed(2)} MB)
                    </p>
                  )}
                </div>
              )}

              {editingBooklet && (
                <div className="md:col-span-2">
                  <label className="label">Replace PDF File (Optional)</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to keep current file</p>
                  {newBooklet.file && (
                    <p className="text-xs text-blue-600 mt-1">
                      New file selected: {newBooklet.file.name} ({(newBooklet.file.size / (1024 * 1024)).toFixed(2)} MB)
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-4">
              <Button type="submit" disabled={uploading}>
                {uploading ? 'Saving...' : (editingBooklet ? 'Update Booklet' : 'Upload Booklet')}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowUploadForm(false);
                setEditingBooklet(null);
                setNewBooklet({
                  title: '',
                  description: '',
                  graduation_year: new Date().getFullYear(),
                  file: null
                });
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Booklets Table with Horizontal Scroll */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : booklets.length === 0 ? (
        <Card className="text-center py-12">
          <div className="text-6xl mb-4">📘</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Booklets Available</h3>
          <p className="text-gray-600">Click "Upload Booklet" to add graduation program booklets.</p>
        </Card>
      ) : (
        <div className="overflow-x-auto shadow rounded-lg">
          <table className="min-w-[1200px] bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Downloads</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {booklets.map((booklet, index) => (
                <tr key={booklet.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 sticky left-0 bg-white">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{booklet.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{booklet.graduation_year || '—'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-xs truncate">{booklet.description || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{booklet.file_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{formatFileSize(booklet.file_size)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{booklet.download_count || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{new Date(booklet.created_at).toLocaleDateString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleDownload(booklet)}
                      className="text-green-600 hover:text-green-900 mr-3"
                      title="Download"
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={() => handleEdit(booklet)}
                      className="text-primary hover:text-primary-dark mr-3"
                      title="Edit"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(booklet.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminBooklets;
